import { SearchState } from "@/main/react/page/search/types";

export function extractSearchResultId(item: any): string | undefined {
    const rawId = item?.publicId ?? item?.userId ?? item?.id ?? item?.discussionPublicId ?? item?.conversationPublicId ?? item?.username;
    return rawId !== undefined && rawId !== null ? String(rawId) : undefined;
}

export function areSearchStatesEqual(left: SearchState, right: SearchState): boolean {
    return left.query === right.query
        && left.type === right.type
        && left.page === right.page
        // `language` is load-bearing: useHistoryBackedState skips the URL push when areEqual is true, so
        // omitting it here makes the search-language picker a no-op (state never changes, URL never gains
        // ?language=…, fetchers stay on "all"). Fixed 2026-07-17.
        && left.language === right.language
        // Same trap, same shape: leaving `questionsOnly` out here makes the Questions chip a no-op.
        && left.questionsOnly === right.questionsOnly
        // And again for SV-142. Optional in the type, so absent and `false` must compare equal.
        && Boolean(left.sensitiveOnly) === Boolean(right.sensitiveOnly)
        // FI-605, the same trap a fourth time: without it the Newest pill is a no-op.
        && (left.order ?? null) === (right.order ?? null);
}

export function buildNextSearchState(
    previous: SearchState,
    patch: Partial<SearchState> | ((previous: SearchState) => SearchState),
): SearchState {
    const draft = typeof patch === "function" ? patch(previous) : patch;
    const resetPage = (draft.query != null && draft.query !== previous.query)
        || (draft.type != null && draft.type !== previous.type);

    return {
        ...previous,
        ...draft,
        query: draft.query ?? previous.query,
        type: draft.type ?? previous.type,
        page: Math.max(1, resetPage && draft.page == null ? 1 : (draft.page ?? previous.page)),
    };
}

/**
 * Splits the auto-advance notice around the name of the vertical the page moved OFF, so that name can
 * be rendered as the way back to it (`EMPTY-STATES-90`).
 *
 * <p>The notice is one interpolated sentence — "No matches in {0}. Showing {1} instead." — and at
 * 412 CSS px the only existing route back to `{0}` is a tab anchor about 39 px past the right edge of
 * a horizontal scroller, which is the least discoverable gesture on touch. So the sentence has to
 * carry its own remedy: whatever names the vertical is the thing that returns to it.
 *
 * <p><b>Why a split and not a second control:</b> the label is already in the sentence, in every
 * locale, and it is already the right word — the same `tabLabels` entry the tab strip itself renders.
 * Anything else is a new key, and a new key is a 25-catalog bill for a word already on screen.
 *
 * <p><b>Returns `null` rather than guessing.</b> Three ways this can be ambiguous, and each of them
 * yields `null` so the caller falls back to today's plain paragraph:
 * <ul>
 *   <li>the two labels are equal, or `toLabel` contains `fromLabel` — then an occurrence of
 *       `fromLabel` may belong to the other placeholder;</li>
 *   <li>`fromLabel` does not occur exactly once — a locale whose template drops or repeats `{0}`;</li>
 *   <li>either input is blank.</li>
 * </ul>
 * Degrading to the current behaviour is always safe; linking the wrong word is not.
 */
export function splitAutoAdvanceNotice(
    notice: string,
    fromLabel: string,
    toLabel: string,
): { before: string; label: string; after: string } | null {
    if (!notice || !fromLabel.trim() || !toLabel.trim()) {
        return null;
    }
    if (fromLabel === toLabel || toLabel.includes(fromLabel)) {
        return null;
    }
    const first = notice.indexOf(fromLabel);
    if (first < 0 || notice.indexOf(fromLabel, first + fromLabel.length) >= 0) {
        return null;
    }
    return {
        before: notice.slice(0, first),
        label: fromLabel,
        after: notice.slice(first + fromLabel.length),
    };
}

/**
 * Whether a `resolveSearch` / `searchResultCounts` response is the server's DEGRADED answer: a
 * statement behind it exceeded the 12-second page query budget and the backend answered a 200 with
 * `timedOut: true` (preferred tab, empty lists, no counts) instead of a 500. Such a response must not
 * seed the cache, paint badges or auto-advance; the page shows a "try a shorter query" notice instead.
 *
 * The generated client predates the field (regenerating it needs a running backend, which is why
 * this reads through a local type extension rather than blocking on `make sync-client-local`).
 * Design: `designs/01-now/2026-08-21-search-resolve-unbounded-statement-design.md` §10.
 */
export function responseTimedOut(response: unknown): boolean {
    return typeof response === "object"
        && response !== null
        && (response as { timedOut?: unknown }).timedOut === true;
}
